import type {
	Suggestion,
	SuggestionCondoResult
} from '~/components/SearchSuggest';
import type { SelectAddressEvent } from '~/composables/useNewSearchSuggest';
import type { FilterApplied } from '~/types/mixpanel';

import { useEvents } from '~/composables/events/useEvents';
import { useAddressesMultiCityMethods } from '~/composables/useAddressesWrapper';
import { filtersAppliedMap } from '~/constants';
import { MixpanelProps } from '~/core/service/mixpanel';
import { PropertyFiltersPresenter } from '~/presenters/PropertyFiltersPresenter';
import { cleanLocationQueryFromPath } from '~/utils/cleanLocationQueryFromPath';
import { nuxtPropertiesRepository } from '~~/adapters/repositories/nuxt';

export const FieldTypeEnum = {
	FIRST: 'first',
	SECOND: 'second'
} as const;

export type FieldType = (typeof FieldTypeEnum)[keyof typeof FieldTypeEnum];

export interface UseSearchSuggestNavigationOptions {
	currentPage?: string;
}

export interface CTAClickParams {
	hasFirstFieldSelection: boolean;
	isSecondFieldEnabled: boolean;
	firstFieldData?: SelectAddressEvent | null;
}

export interface UseSearchSuggestNavigationReturn {
	handleSelectCode: (
		event: Suggestion['searchParams'],
		fieldType?: FieldType
	) => Promise<void>;
	handleSelectCondo: (
		searchParams: SuggestionCondoResult,
		fieldType?: FieldType
	) => void;
	handleSelectPlace: (
		searchParams: SelectAddressEvent,
		fieldType?: FieldType
	) => void;
	handleSelectAddress: (
		event: SelectAddressEvent,
		fieldType?: FieldType
	) => void;
	handleExplorePropertiesCTA: (params: CTAClickParams) => void;
	isFetchingPropertyByCodeSearch: Ref<boolean>;
}

export function useSearchSuggestNavigation(
	options?: UseSearchSuggestNavigationOptions
): UseSearchSuggestNavigationReturn {
	const isFetchingPropertyByCodeSearch = shallowRef(false);
	const { trackFilterApplied } = useEvents();
	const multiCityMethods = useAddressesMultiCityMethods();

	const { currentPage = 'Property Details' } = options || {};

	/**
	 * Helper para gerar propriedades de clique baseado no fieldType
	 */
	function getFieldClickProps(fieldType?: FieldType) {
		if (!fieldType) return {};

		return {
			[MixpanelProps.CLICK_FIRST_FIELD]: fieldType === FieldTypeEnum.FIRST,
			[MixpanelProps.CLICK_SECOND_FIELD]: fieldType === FieldTypeEnum.SECOND,
			[MixpanelProps.CLICK_CTA]: false,
			[MixpanelProps.CLICK_EMPTY]: false
		};
	}

	/**
	 * Simplifica a navegação por código:
	 * - Busca a propriedade pelo código
	 * - Navega para PDP se encontrada (a página faz redirect automático se não estiver publicada)
	 * - Navega para fallback se não encontrada
	 */
	async function handleSelectCode(
		event: Suggestion['searchParams'],
		fieldType?: FieldType
	) {
		if (!event.code) return;

		const code = event.code;
		isFetchingPropertyByCodeSearch.value = true;

		trackFilterApplied({
			[MixpanelProps.FILTERS_APPLIED]: 'code',
			[MixpanelProps.CURRENT_PAGE]: currentPage,
			[MixpanelProps.FILTERED_BY_OPEN_FILTERS]: false,
			[MixpanelProps.FILTERED_BY_SEARCH_BAR]: true,
			[MixpanelProps.FILTERED_BY_QUICK_FILTER]: false,
			[MixpanelProps.FILTERED_BY_MORE_FILTERS]: false,
			[MixpanelProps.REGION_NAME]: '',
			[MixpanelProps.CITY_NAME]: '',
			...getFieldClickProps(fieldType),
			commercial_id: event.code
		} as FilterApplied);

		try {
			const [property, error] = await nuxtPropertiesRepository.checkOne(code);

			if (!property || error) {
				navigateTo('/venda/imoveis/sao-paulo-sp-brasil');
				return;
			}

			// Navega para PDP - a página [[slug]].vue fará redirect automático se não estiver publicada
			navigateTo(`/imovel/${property.commercialId}/${property.slug}`);
		} catch (error) {
			console.error('Error fetching property by code:', error);
			navigateTo('/venda/imoveis/sao-paulo-sp-brasil');
		} finally {
			isFetchingPropertyByCodeSearch.value = false;
		}
	}

	function handleSelectCondo(
		searchParams: SuggestionCondoResult,
		fieldType?: FieldType
	) {
		trackFilterApplied({
			[MixpanelProps.FILTERS_APPLIED]: 'condo',
			[MixpanelProps.CURRENT_PAGE]: currentPage,
			[MixpanelProps.FILTERED_BY_OPEN_FILTERS]: false,
			[MixpanelProps.FILTERED_BY_SEARCH_BAR]: true,
			[MixpanelProps.FILTERED_BY_QUICK_FILTER]: false,
			[MixpanelProps.FILTERED_BY_MORE_FILTERS]: false,
			[MixpanelProps.TEXT_SEARCHED]: searchParams.textSearched,
			[MixpanelProps.CONDO_NAME]: searchParams.condoName,
			...getFieldClickProps(fieldType)
		} as any);

		const base = '/condominio/';
		return navigateTo(`${base}${searchParams.condoSlug}`);
	}

	function handleSelectPlace(
		searchParams: SelectAddressEvent,
		fieldType?: FieldType
	) {
		trackFilterApplied({
			[MixpanelProps.REGION_NAME]: searchParams.macroregions?.[0] || '',
			[MixpanelProps.CITY_NAME]: searchParams.cities?.[0] || '',
			[MixpanelProps.NEIGHBORHOODS_NAME]: searchParams.regions?.[0] || '',
			[MixpanelProps.ADDRESS_OR_POINT_OF_INTEREST_NAME]:
				searchParams.text || '',
			[MixpanelProps.CURRENT_PAGE]: currentPage,
			[MixpanelProps.FILTERED_BY_OPEN_FILTERS]: false,
			[MixpanelProps.FILTERED_BY_SEARCH_BAR]: true,
			[MixpanelProps.FILTERED_BY_QUICK_FILTER]: false,
			[MixpanelProps.FILTERED_BY_MORE_FILTERS]: false,
			[MixpanelProps.FILTERS_APPLIED]:
				filtersAppliedMap[searchParams.searchType ?? 'street'],
			[MixpanelProps.COMMERCIAL_ID]: '',
			...getFieldClickProps(fieldType)
		} as any);

		const filters: any = {
			states: [searchParams.states?.[0] || 'sp']
		};

		// Only include addressSlug when there are coordinates (POI or street address)
		if (searchParams.lat && searchParams.lng) {
			filters.poiLocationLat = searchParams.lat;
			filters.poiLocationLng = searchParams.lng;
			filters.addressSlug = searchParams.text;
		}

		const path = cleanLocationQueryFromPath(
			PropertyFiltersPresenter.getPathFromFilters(filters)
		);

		return navigateTo(path);
	}

	function handleSelectAddress(
		event: SelectAddressEvent,
		fieldType?: FieldType
	) {
		trackFilterApplied({
			[MixpanelProps.REGION_NAME]: event.macroregions?.[0] || '',
			[MixpanelProps.CITY_NAME]: event.cities?.[0] || '',
			[MixpanelProps.NEIGHBORHOODS_NAME]: event.regions?.[0] || '',
			[MixpanelProps.ADDRESS_OR_POINT_OF_INTEREST_NAME]: event.text || '',
			[MixpanelProps.CURRENT_PAGE]: currentPage,
			[MixpanelProps.FILTERED_BY_OPEN_FILTERS]: false,
			[MixpanelProps.FILTERED_BY_SEARCH_BAR]: true,
			[MixpanelProps.FILTERED_BY_QUICK_FILTER]: false,
			[MixpanelProps.FILTERED_BY_MORE_FILTERS]: false,
			[MixpanelProps.FILTERS_APPLIED]:
				filtersAppliedMap[event.searchType ?? 'street'],
			[MixpanelProps.COMMERCIAL_ID]: '',
			...getFieldClickProps(fieldType)
		} as FilterApplied);

		let state = 'sp';

		if (event.states && event.states.length > 0 && event.states[0]) {
			state = event.states[0].toLowerCase();
		} else if (multiCityMethods.getStateByCity && event.cities?.[0]) {
			const detectedState = multiCityMethods.getStateByCity(event.cities[0]);
			if (detectedState) {
				state = detectedState.toLowerCase();
			}
		} else {
			state =
				event.cities?.[0] === 'Curitiba' ||
				event.macroregions?.[0] === 'Curitiba'
					? 'pr'
					: 'sp';
		}

		const filters: any = {
			states: [state ?? 'sp'],
			macroregions: event.macroregions,
			cities: event.cities,
			regions: event.regions
		};

		// Only include addressSlug when there are coordinates (POI or street address)
		if (event.lat && event.lng) {
			filters.poiLocationLat = event.lat;
			filters.poiLocationLng = event.lng;
			filters.addressSlug = event.text;
		}

		const path = cleanLocationQueryFromPath(
			PropertyFiltersPresenter.getPathFromFilters(filters)
		);

		return navigateTo(path);
	}

	function handleExplorePropertiesCTA(params: CTAClickParams) {
		const { hasFirstFieldSelection, isSecondFieldEnabled, firstFieldData } =
			params;

		// Determinar as props baseado no estado
		const clickEmpty = !hasFirstFieldSelection && !isSecondFieldEnabled;
		const clickFirstfield = hasFirstFieldSelection;

		trackFilterApplied({
			[MixpanelProps.CURRENT_PAGE]: currentPage,
			[MixpanelProps.FILTERED_BY_OPEN_FILTERS]: false,
			[MixpanelProps.FILTERED_BY_SEARCH_BAR]: false,
			[MixpanelProps.FILTERED_BY_QUICK_FILTER]: false,
			[MixpanelProps.FILTERED_BY_MORE_FILTERS]: false,
			[MixpanelProps.REGION_NAME]: firstFieldData?.macroregions?.[0] ?? '',
			[MixpanelProps.CITY_NAME]: firstFieldData?.cities?.[0] ?? '',
			[MixpanelProps.NEIGHBORHOODS_NAME]: firstFieldData?.regions?.[0] ?? '',
			[MixpanelProps.ADDRESS_OR_POINT_OF_INTEREST_NAME]:
				firstFieldData?.text ?? '',
			[MixpanelProps.COMMERCIAL_ID]: firstFieldData?.code ?? '',
			[MixpanelProps.FILTERS_APPLIED]: clickEmpty
				? ''
				: filtersAppliedMap[firstFieldData?.searchType ?? 'street'],
			[MixpanelProps.CLICK_SECOND_FIELD]: false,
			[MixpanelProps.CLICK_CTA]: true,
			[MixpanelProps.CLICK_EMPTY]: clickEmpty,
			[MixpanelProps.TEXT_SEARCH]: firstFieldData?.text ?? '',
			[MixpanelProps.CLICK_FIRST_FIELD]: clickFirstfield
		} as FilterApplied);

		return navigateTo('/venda/imoveis/sao-paulo-sp-brasil');
	}

	return {
		handleSelectCode,
		handleSelectCondo,
		handleSelectPlace,
		handleSelectAddress,
		handleExplorePropertiesCTA,
		isFetchingPropertyByCodeSearch
	};
}
